import { TOTP } from 'totp-generator';

export type RotatedBarcode = {
  barcode: string;
  expires: number;
  period: number;
};

const wordMap: { [key: string]: number | string } = {
  subsolar: 2,
  clinch: 2,
  inceptive: 2,
  optical: 3,
  drew: 3,
  shuck: 3,
  culture: 4,
  legging: 4,
  belong: 4,
  limit: 5,
  frost: 5,
  trademark: 5,
  radio: 6,
  new: 6,
  cordless: 6,
  gravitate: 7,
  disembark: 7,
  backstroke: 7,
  '.': '=',
};

/**
 * Decodes a rotation phrase into the TOTP period and base32 secret.
 *
 * The phrase is a space separated list where the first word is the period in seconds and each
 * remaining word maps to a base32 character: known words resolve via the word map, anything else
 * falls back to its own uppercased first letter.
 *
 * @param phrase The phrase to decode
 * @returns The period in seconds and the base32 secret
 * @throws If the phrase is missing, has a non-numeric period, or contains no secret words
 */
export const decodePhrase = (phrase: string): { period: number; secret: string } => {
  if (!phrase) {
    throw new Error('Rotation phrase is empty');
  }

  const words = phrase.split(' ').filter(w => !!w);

  if (words.length < 2) {
    throw new Error('Rotation phrase is too short');
  }

  const period = +words.shift()!;

  if (!Number.isFinite(period) || period <= 0) {
    throw new Error('Rotation phrase has an invalid period');
  }

  const secret = words.map(w => (w in wordMap ? wordMap[w] : w[0].toUpperCase())).join('');

  return { period, secret };
};

/**
 * Rotates a barcode by appending a TOTP token to it.
 *
 * The token is derived from the current time slice, so it stays the same for the whole period and
 * changes when the period rolls over. Both sides compute it independently, so no network call is
 * needed to validate.
 *
 * @param barcode The barcode to rotate
 * @param phrase The phrase to derive the rotation secret from, ie. "30 subsolar frost radio . drew"
 * @param now The timestamp to generate against, defaults to the current device clock
 * @returns The rotated barcode and the expiration time
 * @throws If the phrase cannot be decoded
 */
export const rotateBarcode = async (barcode: string, phrase: string, now: number = Date.now()): Promise<RotatedBarcode> => {
  const { period, secret } = decodePhrase(phrase);

  const { otp, expires } = await TOTP.generate(secret, { period, digits: 6, algorithm: 'SHA-1', timestamp: now });

  return {
    barcode: `${barcode}_${otp}`,
    expires,
    period,
  };
};
